import { Currency, CurrencyAmount, Percent, Token } from '@kodiak-finance/sdk-core'
import { NonfungiblePositionManager } from '@kodiak-finance/v3-sdk'
import Badge from 'components/Badge'
import { ButtonConfirmed, ButtonGray, ButtonPrimary } from 'components/Button'
import { DarkCard, LightCard } from 'components/Card'
import { AutoColumn } from 'components/Column'
import CurrencyLogo from 'components/CurrencyLogo'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import Loader from 'components/Loader'
import { ToggleLabel, ToggleWrap } from 'components/PositionList/styled'
import PoweredByKodiak from 'components/PoweredByKodiak'
import { RowBetween, RowFixed } from 'components/Row'
import { Dots } from 'components/swap/styleds'
import Toggle from 'components/Toggle'
import TransactionConfirmationModal, { ConfirmationModalContent } from 'components/TransactionConfirmationModal'
import { NONFUNGIBLE_POSITION_MANAGER_ADDRESSES } from 'constants/addresses'
import { useTokens } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { useCurrencyId } from 'hooks/useCurrencyId'
import useIsTickAtLimit from 'hooks/useIsTickAtLimit'
import { useProcessedPoolData } from 'hooks/useProcessedPoolData'
import { useSendTransaction } from 'hooks/useTransaction'
import { useV3PositionFees } from 'hooks/useV3PositionFees'
import { useV3PositionFromTokenId } from 'hooks/useV3Positions'
import { useTokenPricing } from 'hooks/useV3TokenPricing'
import useWaitForTransactionReceipt from 'hooks/useWaitForTransactionReceipt'
import useNativeCurrency from 'lib/hooks/useNativeCurrency'
import { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Bound } from 'state/mint/v3/actions'
import { useIsTransactionPending, useTransactionAdder } from 'state/transactions/hooks'
import styled from 'styled-components'
import { ExternalLink, HideExtraSmall, StyledInternalLink, ThemedText } from 'theme'
import { currencyId } from 'utils/currencyId'
import { formatCurrencyAmount } from 'utils/formatCurrencyAmount'
import { formatTickPrice } from 'utils/formatTickPrice'
import { getTickToPrice } from 'utils/getTickToPrice'
import { unwrappedToken } from 'utils/unwrappedToken'
import { Hex } from 'viem'

import RangeBadge from '../../components/Badge/RangeBadge'
import RateToggle from '../../components/RateToggle'
import { SwitchLocaleLink } from '../../components/SwitchLocaleLink'
import useTheme from '../../hooks/useTheme'
import { TransactionType } from '../../state/transactions/types'
import { calculateGasMargin } from '../../utils/calculateGasMargin'
import { ExplorerDataType, getExplorerLink } from '../../utils/getExplorerLink'
import { LoadingRows } from './styleds'

const PageWrapper = styled.div`
  min-width: 800px;
  max-width: 960px;

  padding: 24px 0px;
  gap: 16px;

  max-width: 100%;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    min-width: 680px;
  `};

  ${({ theme }) => theme.mediaWidth.upToSmall`
    min-width: 600px;
  `};

  @media only screen and (max-width: 620px) {
    min-width: 500px;
  }

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    min-width: 340px;
  `};
`

const BadgeText = styled.div`
  font-weight: 500;
  font-size: 14px;
`

// responsive text
// disable the warning because we don't use the end prop, we just want to filter it out
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Label = styled(({ end, ...props }) => <ThemedText.DeprecatedLabel {...props} />)<{ end?: boolean }>`
  display: flex;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 14px; /* 100% */
  justify-content: ${({ end }) => (end ? 'flex-end' : 'flex-start')};
  align-items: center;
`

const ExtentsText = styled.span`
  color: ${({ theme }) => theme.deprecated_text2};
  font-size: 14px;
  text-align: center;
  margin-right: 4px;
  font-weight: 500;
`

const HoverText = styled(ThemedText.DeprecatedMain)`
  text-decoration: none;
  color: ${({ theme }) => theme.deprecated_text3};
  :hover {
    color: ${({ theme }) => theme.deprecated_text1};
    text-decoration: none;
  }
`

const DoubleArrow = styled.span`
  color: ${({ theme }) => theme.deprecated_text3};
  margin: 0 1rem;
`
const ResponsiveRow = styled(RowBetween)`
  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex-direction: column;
    align-items: flex-start;
    row-gap: 16px;
    width: 100%:
  `};
`

const ResponsiveButtonPrimary = styled(ButtonPrimary)`
  border-radius: 8px;
  padding: 8px 16px;
  width: fit-content;
  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex: 1 1 auto;
    width: 49%;
  `};
`

const TempRow = styled(ResponsiveRow)`
  width: 100%;
  gap: 16px;

  &:first-child,
  &:last-child {
    min-height: 226px;
    height: 100%;
  }
`

function CurrentPriceCard({
  currencyQuote,
  currencyBase,
  processedData,
  manuallyInverted = false,
}: {
  currencyQuote?: Currency
  currencyBase?: Currency
  processedData?: any
  manuallyInverted?: boolean
}) {
  const displayPrice = useMemo(() => {
    if (!processedData?.quotePrice || !currencyBase || !currencyQuote) return undefined

    const isCorrectDirection =
      processedData.quotePrice.quoteCurrency.equals(currencyQuote) &&
      processedData.quotePrice.baseCurrency.equals(currencyBase)

    return isCorrectDirection ? processedData.quotePrice : processedData.quotePrice.invert()
  }, [processedData?.quotePrice, currencyBase, currencyQuote])

  return (
    <LightCard padding="12px ">
      <AutoColumn gap="8px" justify="center">
        <ExtentsText>
          <>Current price</>
        </ExtentsText>
        <ThemedText.DeprecatedMediumHeader textAlign="center">
          {displayPrice ? displayPrice.toSignificant(6) : '-'}
        </ThemedText.DeprecatedMediumHeader>
        <ExtentsText>
          <>
            {currencyQuote?.symbol} per {currencyBase?.symbol}
          </>
        </ExtentsText>
      </AutoColumn>
    </LightCard>
  )
}

function LinkedCurrency({ chainId, currency }: { chainId?: number; currency?: Currency }) {
  const address = (currency as Token)?.address

  if (typeof chainId === 'number' && address) {
    return (
      <ExternalLink href={getExplorerLink(chainId, address, ExplorerDataType.TOKEN)}>
        <RowFixed>
          <CurrencyLogo currency={currency} size="20px" style={{ marginRight: '0.5rem' }} />
          <ThemedText.DeprecatedMain>{currency?.symbol} ↗</ThemedText.DeprecatedMain>
        </RowFixed>
      </ExternalLink>
    )
  }

  return (
    <RowFixed>
      <CurrencyLogo currency={currency} size="20px" style={{ marginRight: '0.5rem' }} />
      <ThemedText.DeprecatedMain>{currency?.symbol}</ThemedText.DeprecatedMain>
    </RowFixed>
  )
}

function usePriceBounds({
  token0,
  token1,
  tickLower,
  tickUpper,
  invert,
}: {
  token0?: Token
  token1?: Token
  tickLower?: number
  tickUpper?: number
  invert?: boolean
}) {
  return useMemo(() => {
    if (!token0 || !token1 || tickLower == null || tickUpper == null) {
      return {
        priceLower: undefined,
        priceUpper: undefined,
        base: undefined,
        quote: undefined,
      }
    }

    const base = invert ? token1 : token0
    const quote = invert ? token0 : token1

    let priceA, priceB
    try {
      priceA = getTickToPrice(base, quote, tickLower)
      priceB = getTickToPrice(base, quote, tickUpper)
    } catch (err) {
      console.error('Error calculating tick prices:', err)
      return {
        priceLower: undefined,
        priceUpper: undefined,
        base,
        quote,
      }
    }

    if (!priceA || !priceB) {
      return {
        priceLower: undefined,
        priceUpper: undefined,
        base,
        quote,
      }
    }

    const [priceLower, priceUpper] = priceA.lessThan(priceB) ? [priceA, priceB] : [priceB, priceA]

    return { priceLower, priceUpper, base, quote }
  }, [token0, token1, tickLower, tickUpper, invert])
}

export function PositionPage() {
  const { tokenId: tokenIdFromUrl } = useParams<{ tokenId?: string }>()
  const { chainId, account, wagmiPublicClient } = useConnection()
  const { sendTransactionAsync } = useSendTransaction()
  const waitForTransactionReceipt = useWaitForTransactionReceipt()
  const theme = useTheme()

  const nativeCurrency = useNativeCurrency()

  // flag for receiving WETH
  const [receiveWETH, setReceiveWETH] = useState(false)
  const nativeWrappedSymbol = nativeCurrency.wrapped.symbol

  const parsedTokenId = tokenIdFromUrl ? tokenIdFromUrl : undefined
  const { loading, position: positionDetails } = useV3PositionFromTokenId(parsedTokenId)

  console.debug('Debug: position ticks', positionDetails?.tickLower, positionDetails?.tickUpper)
  const {
    token0: token0Address,
    token1: token1Address,
    fee: feeAmount,
    liquidity,
    tickLower,
    tickUpper,
    tokenId,
    poolTick,
    inRange,
    pool,
    owner,
  } = positionDetails || {}

  const tokenAddresses = useMemo(() => [token0Address, token1Address], [token0Address, token1Address])
  const [token0, token1] = useTokens(tokenAddresses)

  const [feeValue0, feeValue1] = useV3PositionFees({
    position: positionDetails,
    token0,
    token1,
    asWETH: receiveWETH,
  })

  const removed = BigInt(liquidity ?? '0') === 0n

  const {
    pricesLoading,
    fiatValueOfFees,
    fiatValueOfLiquidity,
    amount0ForDisplay: amount0,
    amount1ForDisplay: amount1,
    tokenPriceMap,
  } = useTokenPricing({
    token0,
    token1,
    feeValue0: feeValue0 as CurrencyAmount<Token> | undefined,
    feeValue1: feeValue1 as CurrencyAmount<Token> | undefined,
    liquidity: liquidity || '0',
    tickLower: tickLower ?? 0,
    tickUpper: tickUpper ?? 0,
    currentTick: poolTick ?? 0,
    feeAmount,
    poolLiquidity: pool?.liquidity,
    sqrtPriceX96: pool?.sqrtPrice,
    isClosedPosition: removed,
  })

  const fallbackData = useMemo(() => {
    if (typeof tickLower !== 'number' || typeof tickUpper !== 'number' || typeof poolTick !== 'number') {
      return undefined
    }

    return {
      lowerTick: tickLower,
      upperTick: tickUpper,
      currentTick: poolTick,
      feeTier: feeAmount,
      poolType: 'V3' as const,
    }
  }, [tickLower, tickUpper, poolTick, feeAmount])

  const processedData = useProcessedPoolData(undefined, token0, token1, fallbackData)

  const currency0 = token0 ? unwrappedToken(token0) : undefined
  const currency1 = token1 ? unwrappedToken(token1) : undefined
  const currency0Id = useCurrencyId(currency0)
  const currency1Id = useCurrencyId(currency1)

  const tickAtLimit = useIsTickAtLimit(feeAmount, tickLower, tickUpper)

  const [manuallyInverted, setManuallyInverted] = useState(false)

  const {
    priceLower: displayPriceLower,
    priceUpper: displayPriceUpper,
    base: currencyBase,
    quote: currencyQuote,
  } = usePriceBounds({
    token0: token0?.wrapped,
    token1: token1?.wrapped,
    tickLower,
    tickUpper,
    invert: manuallyInverted,
  })

  const { actualToken0Percentage, actualToken1Percentage } = useMemo(() => {
    if (removed || !amount0 || !amount1) {
      return { actualToken0Percentage: 0, actualToken1Percentage: 0 }
    }

    const hasToken0 = amount0.greaterThan(0)
    const hasToken1 = amount1.greaterThan(0)

    if (hasToken0 && !hasToken1) {
      return { actualToken0Percentage: 100, actualToken1Percentage: 0 }
    } else if (!hasToken0 && hasToken1) {
      return { actualToken0Percentage: 0, actualToken1Percentage: 100 }
    }

    if (hasToken0 && hasToken1 && token0 && token1) {
      const token0Price = tokenPriceMap[token0.address]
      const token1Price = tokenPriceMap[token1.address]

      if (token0Price && token1Price && !pricesLoading) {
        try {
          const amount0USD = parseFloat(amount0.toExact()) * token0Price
          const amount1USD = parseFloat(amount1.toExact()) * token1Price
          const totalUSD = amount0USD + amount1USD

          if (totalUSD > 0) {
            const token0Pct = Math.round((amount0USD / totalUSD) * 100)
            return { actualToken0Percentage: token0Pct, actualToken1Percentage: 100 - token0Pct }
          }
        } catch (error) {
          console.error('Error calculating USD percentages:', error)
        }
      }

      return { actualToken0Percentage: 0, actualToken1Percentage: 0 }
    }

    return { actualToken0Percentage: 0, actualToken1Percentage: 0 }
  }, [amount0, amount1, tokenPriceMap, token0, token1, pricesLoading, removed])

  const currency0ForFeeCollectionPurposes = liquidity ? (receiveWETH ? token0 : unwrappedToken(token0)) : undefined
  const currency1ForFeeCollectionPurposes = liquidity ? (receiveWETH ? token1 : unwrappedToken(token1)) : undefined

  const [collecting, setCollecting] = useState<boolean>(false)
  const [collectingWaiting, setCollectingWaiting] = useState<boolean>(false)
  const [collectMigrationHash, setCollectMigrationHash] = useState<string | null>(null)
  const isCollectPending = useIsTransactionPending(collectMigrationHash ?? undefined)
  const [showConfirm, setShowConfirm] = useState(false)

  const addTransaction = useTransactionAdder()
  const positionManagerAddress = NONFUNGIBLE_POSITION_MANAGER_ADDRESSES[chainId]

  const collect = useCallback(async () => {
    if (
      !currency0ForFeeCollectionPurposes ||
      !currency1ForFeeCollectionPurposes ||
      !chainId ||
      !positionManagerAddress ||
      !account ||
      !tokenId ||
      !wagmiPublicClient
    )
      return

    setCollecting(true)

    // we fall back to expecting 0 fees in case the fetch fails, which is safe in the
    // vast majority of cases
    const { calldata, value } = NonfungiblePositionManager.collectCallParameters({
      tokenId,
      expectedCurrencyOwed0: feeValue0 ?? CurrencyAmount.fromRawAmount(currency0ForFeeCollectionPurposes, 0),
      expectedCurrencyOwed1: feeValue1 ?? CurrencyAmount.fromRawAmount(currency1ForFeeCollectionPurposes, 0),
      recipient: account,
    })

    const txn = {
      to: positionManagerAddress,
      data: calldata as Hex,
      value: BigInt(value),
      account,
    }

    const estimate = await wagmiPublicClient.estimateGas(txn)

    const hash = await sendTransactionAsync({
      to: txn.to,
      data: txn.data,
      gas: calculateGasMargin(estimate as bigint) as bigint,
      value: txn.value,
    })

    const response = { hash }

    setCollectMigrationHash(response.hash)
    setCollecting(false)
    setCollectingWaiting(true)

    addTransaction(response, {
      type: TransactionType.COLLECT_FEES,
      currencyId0: currencyId(currency0ForFeeCollectionPurposes),
      currencyId1: currencyId(currency1ForFeeCollectionPurposes),
    })

    const receipt = await waitForTransactionReceipt({ hash })

    setCollectMigrationHash(receipt.transactionHash)
    setCollectingWaiting(false)
  }, [
    chainId,
    feeValue0,
    feeValue1,
    currency0ForFeeCollectionPurposes,
    currency1ForFeeCollectionPurposes,
    positionManagerAddress,
    account,
    tokenId,
    addTransaction,
    wagmiPublicClient,
    sendTransactionAsync,
    waitForTransactionReceipt,
  ])

  const ownsNFT = Boolean(account && (owner === account.toLowerCase() || positionDetails?.operator === account))

  const feeValueUpper = manuallyInverted ? feeValue0 : feeValue1
  const feeValueLower = manuallyInverted ? feeValue1 : feeValue0

  function modalHeader() {
    return (
      <AutoColumn gap="md" style={{ marginTop: '20px' }}>
        <LightCard padding="12px 16px">
          <AutoColumn gap="md">
            <RowBetween>
              <RowFixed>
                <CurrencyLogo currency={feeValueUpper?.currency} size="20px" style={{ marginRight: '0.5rem' }} />
                <ThemedText.DeprecatedMain>
                  {feeValueUpper ? formatCurrencyAmount(feeValueUpper, 4) : '-'}
                </ThemedText.DeprecatedMain>
              </RowFixed>
              <ThemedText.DeprecatedMain>{feeValueUpper?.currency?.symbol}</ThemedText.DeprecatedMain>
            </RowBetween>
            <RowBetween>
              <RowFixed>
                <CurrencyLogo currency={feeValueLower?.currency} size="20px" style={{ marginRight: '0.5rem' }} />
                <ThemedText.DeprecatedMain>
                  {feeValueLower ? formatCurrencyAmount(feeValueLower, 4) : '-'}
                </ThemedText.DeprecatedMain>
              </RowFixed>
              <ThemedText.DeprecatedMain>{feeValueLower?.currency?.symbol}</ThemedText.DeprecatedMain>
            </RowBetween>
          </AutoColumn>
        </LightCard>
        <ThemedText.DeprecatedItalic>
          <>Collecting fees will withdraw currently available fees for you.</>
        </ThemedText.DeprecatedItalic>
        <ButtonPrimary onClick={collect}>
          <>Collect</>
        </ButtonPrimary>
      </AutoColumn>
    )
  }

  const showCollectAsWeth = Boolean(
    ownsNFT &&
      (feeValue0?.greaterThan(0) || feeValue1?.greaterThan(0)) &&
      currency0 &&
      currency1 &&
      (currency0.isNative || currency1.isNative) &&
      !collectMigrationHash
  )

  return loading || !feeAmount ? (
    <LoadingRows>
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
      <div />
    </LoadingRows>
  ) : (
    <>
      <PageWrapper>
        <TransactionConfirmationModal
          isOpen={showConfirm}
          onDismiss={() => setShowConfirm(false)}
          attemptingTxn={collecting}
          hash={collectMigrationHash ?? ''}
          transactionWaiting={collectingWaiting}
          content={() => (
            <ConfirmationModalContent
              title={<>Claim fees</>}
              onDismiss={() => setShowConfirm(false)}
              topContent={modalHeader}
            />
          )}
          pendingText={<>Collecting fees</>}
        />
        <AutoColumn gap="md">
          <AutoColumn gap="sm">
            <StyledInternalLink
              data-cy="visit-pool"
              style={{ textDecoration: 'none', width: 'fit-content', marginBottom: '0.5rem' }}
              to="/liquidity/v3"
            >
              <HoverText>
                <>← Back to Pools Overview</>
              </HoverText>
            </StyledInternalLink>
            <ResponsiveRow>
              <RowFixed>
                <DoubleCurrencyLogo
                  currency0={currencyBase ?? undefined}
                  currency1={currencyQuote ?? undefined}
                  size={24}
                  margin={true}
                />
                <ThemedText.DeprecatedLabel fontSize="24px" mr="10px">
                  &nbsp;{currencyQuote?.symbol}&nbsp;/&nbsp;{currencyBase?.symbol}
                </ThemedText.DeprecatedLabel>
                <Badge style={{ marginRight: '8px' }}>
                  <BadgeText>
                    <>{new Percent(feeAmount, 1_000_000).toSignificant()}%</>
                  </BadgeText>
                </Badge>
                <RangeBadge removed={removed} inRange={inRange} />
              </RowFixed>
              <RowFixed>
                {ownsNFT && currency0 && currency1 && feeAmount && tokenId && (
                  <RowFixed>
                    <ButtonGray
                      as={StyledInternalLink}
                      to={`/liquidity/v3/increase/${currency0Id}/${currency1Id}/${feeAmount}/${tokenId}`}
                      width="fit-content"
                      padding="8px 16px"
                      $borderRadius="6px"
                      style={{ marginRight: '8px' }}
                    >
                      <>Increase Liquidity</>
                    </ButtonGray>

                    {!removed && (
                      <ResponsiveButtonPrimary
                        as={StyledInternalLink}
                        to={`/liquidity/v3/remove/${tokenId}`}
                        width="fit-content"
                        padding="8px 16px"
                        $borderRadius="6px"
                      >
                        <>Remove Liquidity</>
                      </ResponsiveButtonPrimary>
                    )}
                  </RowFixed>
                )}
              </RowFixed>
            </ResponsiveRow>
            <RowBetween></RowBetween>
          </AutoColumn>
          <TempRow align="flex-start">
            <DarkCard>
              <AutoColumn gap="md" style={{ width: '100%' }}>
                <AutoColumn gap="md">
                  <Label>
                    <>Liquidity</>
                  </Label>
                  {pricesLoading ? (
                    <ThemedText.DeprecatedLargeHeader fontSize="24px" fontWeight={500}>
                      <Loader />
                    </ThemedText.DeprecatedLargeHeader>
                  ) : fiatValueOfLiquidity && fiatValueOfLiquidity > 0.001 ? (
                    <ThemedText.DeprecatedLargeHeader fontSize="24px" fontWeight={500}>
                      ${fiatValueOfLiquidity.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    </ThemedText.DeprecatedLargeHeader>
                  ) : (
                    <ThemedText.DeprecatedLargeHeader color={theme.textPrimary} fontSize="24px" fontWeight={500}>
                      <>$-</>
                    </ThemedText.DeprecatedLargeHeader>
                  )}
                </AutoColumn>
                <LightCard padding="12px 16px">
                  <AutoColumn gap="md">
                    <RowBetween>
                      <LinkedCurrency chainId={chainId} currency={currencyQuote ?? undefined} />
                      <RowFixed>
                        <ThemedText.DeprecatedMain>
                          {currencyQuote?.wrapped?.equals(token0?.wrapped)
                            ? amount0?.toSignificant(4)
                            : amount1?.toSignificant(4)}
                        </ThemedText.DeprecatedMain>
                        {typeof actualToken0Percentage === 'number' && !removed ? (
                          <Badge style={{ marginLeft: '10px' }}>
                            <ThemedText.DeprecatedMain fontSize={11}>
                              <>
                                {currencyQuote?.wrapped?.equals(token0?.wrapped)
                                  ? actualToken0Percentage
                                  : actualToken1Percentage}
                                %
                              </>
                            </ThemedText.DeprecatedMain>
                          </Badge>
                        ) : null}
                      </RowFixed>
                    </RowBetween>
                    <RowBetween>
                      <LinkedCurrency chainId={chainId} currency={currencyBase ?? undefined} />
                      <RowFixed>
                        <ThemedText.DeprecatedMain>
                          {currencyBase?.wrapped?.equals(token0?.wrapped)
                            ? amount0?.toSignificant(4)
                            : amount1?.toSignificant(4)}
                        </ThemedText.DeprecatedMain>
                        {typeof actualToken1Percentage === 'number' && !removed ? (
                          <Badge style={{ marginLeft: '10px' }}>
                            <ThemedText.DeprecatedMain color={theme.deprecated_text2} fontSize={11}>
                              <>
                                {currencyBase?.wrapped?.equals(token0?.wrapped)
                                  ? actualToken0Percentage
                                  : actualToken1Percentage}
                                %
                              </>
                            </ThemedText.DeprecatedMain>
                          </Badge>
                        ) : null}
                      </RowFixed>
                    </RowBetween>
                  </AutoColumn>
                </LightCard>
              </AutoColumn>
            </DarkCard>
            <DarkCard>
              <AutoColumn gap="md" style={{ width: '100%' }}>
                <AutoColumn gap="md">
                  <RowBetween style={{ alignItems: 'flex-start' }}>
                    <AutoColumn gap="md">
                      <Label>
                        <>Unclaimed fees</>
                      </Label>
                      {pricesLoading ? (
                        <ThemedText.DeprecatedLargeHeader
                          color={theme.deprecated_text1}
                          fontSize="24px"
                          fontWeight={500}
                        >
                          <Loader />
                        </ThemedText.DeprecatedLargeHeader>
                      ) : fiatValueOfFees && fiatValueOfFees > 0.0001 ? (
                        <ThemedText.DeprecatedLargeHeader
                          color={theme.deprecated_green1}
                          fontSize="24px"
                          fontWeight={500}
                        >
                          ${fiatValueOfFees.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        </ThemedText.DeprecatedLargeHeader>
                      ) : (
                        <ThemedText.DeprecatedLargeHeader
                          color={theme.deprecated_text1}
                          fontSize="24px"
                          fontWeight={500}
                        >
                          <>$-</>
                        </ThemedText.DeprecatedLargeHeader>
                      )}
                    </AutoColumn>
                    {ownsNFT && (feeValue0?.greaterThan(0) || feeValue1?.greaterThan(0) || !!collectMigrationHash) ? (
                      <ButtonConfirmed
                        disabled={collecting || collectingWaiting || !!collectMigrationHash}
                        confirmed={!!collectMigrationHash && !isCollectPending && !collectingWaiting}
                        width="fit-content"
                        style={{ borderRadius: '6px' }}
                        padding="4px 8px"
                        onClick={() => setShowConfirm(true)}
                      >
                        {!!collectMigrationHash && !isCollectPending && !collectingWaiting ? (
                          <ThemedText.DeprecatedMain color={theme.deprecated_text1}>
                            <> Collected</>
                          </ThemedText.DeprecatedMain>
                        ) : isCollectPending || collecting || collectingWaiting ? (
                          <ThemedText.DeprecatedMain color={theme.deprecated_text1}>
                            {' '}
                            <Dots>
                              <>Collecting</>
                            </Dots>
                          </ThemedText.DeprecatedMain>
                        ) : (
                          <>
                            <ThemedText.DeprecatedMain color={theme.deprecated_white}>
                              <>Collect fees</>
                            </ThemedText.DeprecatedMain>
                          </>
                        )}
                      </ButtonConfirmed>
                    ) : null}
                  </RowBetween>
                </AutoColumn>
                <LightCard padding="12px 16px">
                  <AutoColumn gap="md">
                    <RowBetween>
                      <RowFixed>
                        <CurrencyLogo
                          currency={feeValueUpper?.currency}
                          size="20px"
                          style={{ marginRight: '0.5rem' }}
                        />
                        <ThemedText.DeprecatedMain>{feeValueUpper?.currency?.symbol}</ThemedText.DeprecatedMain>
                      </RowFixed>
                      <RowFixed>
                        <ThemedText.DeprecatedMain>
                          {feeValueUpper ? formatCurrencyAmount(feeValueUpper, 4) : '-'}
                        </ThemedText.DeprecatedMain>
                      </RowFixed>
                    </RowBetween>
                    <RowBetween>
                      <RowFixed>
                        <CurrencyLogo
                          currency={feeValueLower?.currency}
                          size="20px"
                          style={{ marginRight: '0.5rem' }}
                        />
                        <ThemedText.DeprecatedMain>{feeValueLower?.currency?.symbol}</ThemedText.DeprecatedMain>
                      </RowFixed>
                      <RowFixed>
                        <ThemedText.DeprecatedMain>
                          {feeValueLower ? formatCurrencyAmount(feeValueLower, 4) : '-'}
                        </ThemedText.DeprecatedMain>
                      </RowFixed>
                    </RowBetween>
                  </AutoColumn>
                </LightCard>
                {showCollectAsWeth && (
                  <AutoColumn gap="md">
                    <ToggleWrap style={{ width: '100%' }}>
                      <RowBetween>
                        <ToggleLabel>
                          <>Collect as {nativeWrappedSymbol}</>
                        </ToggleLabel>
                        <Toggle
                          id="desktop-hide-closed-positions"
                          isActive={receiveWETH}
                          useAlternateBackground={true}
                          toggle={() => setReceiveWETH((receiveWETH) => !receiveWETH)}
                        />
                      </RowBetween>
                    </ToggleWrap>
                  </AutoColumn>
                )}
              </AutoColumn>
            </DarkCard>
          </TempRow>
          <DarkCard>
            <AutoColumn gap="md">
              <RowBetween>
                <RowFixed>
                  <Label display="flex" style={{ marginRight: '12px' }}>
                    <>Price range</>
                  </Label>
                  <HideExtraSmall>
                    <>
                      <RangeBadge removed={removed} inRange={inRange} />
                      <span style={{ width: '8px' }} />
                    </>
                  </HideExtraSmall>
                </RowFixed>
                <RowFixed>
                  {currencyBase && currencyQuote && (
                    <RateToggle
                      currencyA={currencyBase}
                      currencyB={currencyQuote}
                      handleRateToggle={() => setManuallyInverted(!manuallyInverted)}
                    />
                  )}
                </RowFixed>
              </RowBetween>

              <RowBetween>
                <LightCard padding="12px" width="100%">
                  <AutoColumn gap="8px" justify="center">
                    <ExtentsText>
                      <>Min price</>
                    </ExtentsText>
                    <ThemedText.DeprecatedMediumHeader textAlign="center">
                      {formatTickPrice(displayPriceLower, tickAtLimit, Bound.LOWER, feeAmount)}
                    </ThemedText.DeprecatedMediumHeader>
                    <ExtentsText>
                      {' '}
                      <>
                        {currencyQuote?.symbol} per {currencyBase?.symbol}
                      </>
                    </ExtentsText>

                    {inRange && (
                      <ThemedText.DeprecatedSmall color={theme.deprecated_text3}>
                        <>Your position will be 100% {currencyBase?.symbol} at this price.</>
                      </ThemedText.DeprecatedSmall>
                    )}
                  </AutoColumn>
                </LightCard>

                <DoubleArrow>⟷</DoubleArrow>
                <LightCard padding="12px" width="100%">
                  <AutoColumn gap="8px" justify="center">
                    <ExtentsText>
                      <>Max price</>
                    </ExtentsText>
                    <ThemedText.DeprecatedMediumHeader textAlign="center">
                      {formatTickPrice(displayPriceUpper, tickAtLimit, Bound.UPPER, feeAmount)}
                    </ThemedText.DeprecatedMediumHeader>
                    <ExtentsText>
                      {' '}
                      <>
                        {currencyQuote?.symbol} per {currencyBase?.symbol}
                      </>
                    </ExtentsText>

                    {inRange && (
                      <ThemedText.DeprecatedSmall color={theme.deprecated_text3}>
                        <>Your position will be 100% {currencyQuote?.symbol} at this price.</>
                      </ThemedText.DeprecatedSmall>
                    )}
                  </AutoColumn>
                </LightCard>
              </RowBetween>
              <CurrentPriceCard
                currencyQuote={currencyQuote ?? undefined}
                currencyBase={currencyBase ?? undefined}
                processedData={processedData}
                manuallyInverted={manuallyInverted}
              />
            </AutoColumn>
          </DarkCard>
        </AutoColumn>
      </PageWrapper>
      <PoweredByKodiak />

      <SwitchLocaleLink />
    </>
  )
}
